"use client";

import { ArrowUpRight } from "lucide-react";
import Link from "next/link";

import { cn } from "@/lib/utils";

import { SiteHeader } from "@/components/shell/site-header";

import { ProgressSweep } from "@/features/auth/components/admin-login/admin-login-shared";
import { CheckRow, FieldError } from "@/features/auth/components/auth-fields";
import {
  LOGIN_FORGOT,
  LOGIN_LABELS,
  LOGIN_NO_ACCOUNT,
  LOGIN_REMEMBER_ME,
  LOGIN_RESEND,
  LOGIN_SIGNUP_LINK,
  LOGIN_SUBMIT,
  LOGIN_SUBMITTING,
  LOGIN_TITLE,
  type LoginScenario,
} from "@/features/auth/components/login/login-content";
import { useLoginPage } from "@/features/auth/components/login/login-model";

/**
 * `/login` — the sign-in screen.
 *
 * Promoted from the four-way design bake-off ("Split panel", Railway) once it
 * was approved; A, C and D and the `?variant=` harness are gone. It replaced an
 * `AuthCard` + `LoginForm` page that had rewritten every string ("Welcome back",
 * "Sign in to your Xgodo account", "Sign in", "Forgot password?") and dropped
 * the "Resend activation email" link the live page carries.
 *
 * A full-height two-column split: the brand panel on the LEFT carrying the page
 * heading and the "Sign Up" line, the form on the RIGHT. Both halves reach the
 * viewport edges, so there is no dead space at any width.
 *
 * It was built mirrored (form left) to set it apart from `/signup` and
 * `/admin/login`, and swapped to this orientation on review — the panel-left
 * reading won. Everything moved with its side: the panel's bloom sits at its
 * top-LEFT and its linear gradient runs 160deg, the seam is on the panel's right
 * edge, and the form half's weaker bloom is anchored at its 0% edge so the
 * panel's light still carries across that seam rather than stopping at it.
 *
 * The page heading lives in the brand panel, not above the form: the form is
 * two fields, and giving it its own title on a screen this large made it look
 * like a fragment. The two utility links sit together as a pair beneath the
 * button, each an arrowed row — grouped, because they're the same kind of thing
 * (a way out when the password or the activation email is the problem).
 */
export function LoginView({ scenario }: { scenario: LoginScenario }) {
  const { form, submitting, formError, onSubmit } = useLoginPage(scenario);
  const { register, formState } = form;
  const errors = formState.errors;

  const well =
    "h-12 w-full rounded-xl border border-input bg-elevated px-4 text-[15px] text-foreground outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-muted-foreground hover:border-border focus:border-primary focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.22)] disabled:opacity-40 disabled:hover:border-input";
  const labelClass = "mb-2 block text-[13px] font-medium text-muted-foreground";
  const utilityLink =
    "group flex items-center justify-between rounded-xl border border-hairline px-4 py-3 text-[13px] text-muted-foreground transition-colors duration-150 hover:border-border hover:text-foreground";

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background font-sans text-foreground">
      <SiteHeader />

      <div className="grid flex-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        {/* Brand panel — now the left half */}
        <aside
          className="relative flex flex-col overflow-hidden px-8 py-12 lg:px-14 lg:py-14"
          style={{
            background:
              "hsl(var(--card))",
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 hidden w-px lg:block"
            style={{
              background:
                "hsl(var(--hairline))",
            }}
          />

          <div className="relative my-auto">
            <h1 className="text-4xl font-bold tracking-tight lg:text-[3.25rem] lg:leading-[1.02]">
              {LOGIN_TITLE}
            </h1>
            <div className="mt-6 h-px w-16 bg-primary" />
            <p className="mt-7 text-sm text-muted-foreground">
              {LOGIN_NO_ACCOUNT}{" "}
              <Link
                href="/signup"
                className="font-medium text-primary-text underline-offset-4 transition-colors hover:text-foreground hover:underline"
              >
                {LOGIN_SIGNUP_LINK}
              </Link>
            </p>
          </div>
        </aside>
        <main
          id="main-content"
          className="relative grid place-items-center overflow-hidden px-6 py-14 lg:px-16"
        >
          {/* The brand panel's light carried across the seam, weaker, so the
           * two halves read as one field rather than two panels. Anchored at
           * this panel's 0% edge — the side the panel is now on. */}

          <div className="relative w-full max-w-[25rem]">
            {formError ? (
              <p
                role="alert"
                className="mb-6 rounded-xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive-text"
              >
                {formError}
              </p>
            ) : null}

            <form onSubmit={onSubmit} noValidate className="space-y-5">
              <div>
                <label htmlFor="b-username" className={labelClass}>
                  {LOGIN_LABELS.username}
                </label>
                <input
                  id="b-username"
                  autoComplete="username"
                  disabled={submitting}
                  className={well}
                  {...register("username")}
                />
                <FieldError message={errors.username?.message} />
              </div>

              <div>
                <label htmlFor="b-password" className={labelClass}>
                  {LOGIN_LABELS.password}
                </label>
                <input
                  id="b-password"
                  type="password"
                  autoComplete="current-password"
                  disabled={submitting}
                  className={well}
                  {...register("password")}
                />
                <FieldError message={errors.password?.message} />
              </div>

              <CheckRow
                id="b-remember"
                label={LOGIN_REMEMBER_ME}
                disabled={submitting}
                labelClassName="text-[13px] text-muted-foreground"
                registration={register("rememberMe")}
              />

              <div>
                <button
                  type="submit"
                  disabled={submitting}
                  className={cn(
                    "mt-1 h-12 w-full rounded-lg bg-primary text-sm font-semibold text-primary-foreground",
                    "transition-colors duration-150 hover:bg-primary active:bg-primary",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    "disabled:cursor-not-allowed disabled:bg-primary/45",
                  )}
                >
                  {submitting ? LOGIN_SUBMITTING : LOGIN_SUBMIT}
                </button>
                <ProgressSweep
                  active={submitting}
                  className="mt-3 rounded-full"
                  barClassName="via-primary"
                />
                <span role="status" aria-live="polite" className="sr-only">
                  {submitting ? "Logging in" : ""}
                </span>
              </div>
            </form>

            {/* The two escape hatches, grouped: same shape, same weight. */}
            <div className="mt-8 space-y-2">
              <Link href="/forgot" className={utilityLink}>
                {LOGIN_FORGOT}
                <ArrowUpRight
                  aria-hidden="true"
                  className="size-4 text-muted-foreground transition-colors duration-150 group-hover:text-primary-text"
                />
              </Link>
              <Link href="/signup/resend_activation" className={utilityLink}>
                {LOGIN_RESEND}
                <ArrowUpRight
                  aria-hidden="true"
                  className="size-4 text-muted-foreground transition-colors duration-150 group-hover:text-primary-text"
                />
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
